import { create } from 'zustand';
import { entitlementsApi, type EntitlementSnapshot } from '../api/endpoints/entitlements';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface EntitlementState {
  snapshot: EntitlementSnapshot | null;
  status: Status;
  error: string | null;
  hydrate: () => Promise<void>;
  refresh: () => Promise<EntitlementSnapshot | null>;
  reset: () => void;
}

export const useEntitlementStore = create<EntitlementState>((set, get) => ({
  snapshot: null,
  status: 'idle',
  error: null,

  hydrate: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading', error: null });
    try {
      const snapshot = await entitlementsApi.me();
      set({ snapshot, status: 'ready' });
    } catch (err) {
      set({
        status: 'error',
        error: err instanceof Error ? err.message : '加载会员状态失败',
      });
    }
  },

  refresh: async () => {
    try {
      const snapshot = await entitlementsApi.me();
      set({ snapshot, status: 'ready', error: null });
      return snapshot;
    } catch (err) {
      set({
        status: 'error',
        error: err instanceof Error ? err.message : '刷新会员状态失败',
      });
      return null;
    }
  },

  reset: () => set({ snapshot: null, status: 'idle', error: null }),
}));

/**
 * 工具：判断当前是否拥有 active/trialing/grace 的活跃订阅。
 * 用于在路由守卫、UI 引导处快速判断 Pro 状态。
 */
export function hasActiveProSubscription(snapshot: EntitlementSnapshot | null): boolean {
  if (!snapshot) return false;
  if (snapshot.plan?.tier === 'pro') return true;
  return snapshot.active_subscriptions.some((sub) =>
    ['active', 'trialing', 'grace'].includes(sub.status),
  );
}
